<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Radio, Text } from '@Shelly/shelly-design-system/index';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { app, os } from 'src/stores/session/native-bridge';
import { hideBluetoothSettingsToast } from 'src/stores/ui/calibration-modals';
import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import { bulkUpdate } from '../ApplicationSettings/app_settings_helper';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
	import { calculateDistValues } from 'src/helpers/distance_helper';
	import { real_id } from 'src/helpers/interface_helpers';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;

	let distanceRange = device?.distanceRange ? `${device.distanceRange}` : '7';
	let initialized = false;

	const setDistanceRange = () => {
		hideBluetoothSettingsToast.set(false);
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_general_ios(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					)
				: control_bluetooth_general_android(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					);

			bulkUpdate(device.id, { distanceRange });
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';

	$: closeStart = calculateDistValues(200, distanceUnit);
	$: closeEnd = calculateDistValues(450, distanceUnit, true);
	$: midStart = calculateDistValues(300, distanceUnit);
	$: midEnd = calculateDistValues(3500, distanceUnit, true);
	$: farStart = calculateDistValues(2500, distanceUnit);
	$: farEnd = calculateDistValues(5000, distanceUnit, true);

	$: switch ($controlBleDevice.currentStatusIndex) {
		// after receiving one of the statuses below remove the initialized state
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			initialized = false;
			break;
		// after receiving one of the statuses below add the initialized state
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
		case BLU_STATUS.DEVICE_PROCESSED:
		case BLU_STATUS.DISTANCE_RANGE_SET:
		case BLU_STATUS.INTERVAL_MEASURE_SET:
		case BLU_STATUS.MEASURE_VIBRATION_ENABLED:
		case BLU_STATUS.MEASURE_VIBRATION_DISABLED:
			initialized = true;
			break;
		default:
			break;
	}
</script>

<Text styleType="body-small" class="mb-s">
	{$format('text_set_distance_range', {
		default: 'Set the distance range that the device measures.',
	})}
</Text>

<Radio
	bind:selected={distanceRange}
	value="1"
	size="m"
	info={`${closeStart} - ${closeEnd}`}
	class="mb-s"
	id="radio-lnx2y4o"
>
	{$format('distance_range_option_close', { default: 'Close' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="2"
	size="m"
	info={`${midStart} - ${midEnd}`}
	class="mb-s"
	id="radio-crmbuvn"
>
	{$format('distance_range_option_mid', { default: 'Mid' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="3"
	size="m"
	info={`${closeStart} - ${midEnd}`}
	class="mb-s"
	id="radio-sco8ydh"
>
	{$format('distance_range_option_close_mid', { default: 'Close and Mid' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="4"
	size="m"
	info={`${farStart} - ${farEnd}`}
	class="mb-s"
	id="radio-l22lwg9"
>
	{$format('distance_range_option_far', { default: 'Far' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="5"
	size="m"
	info={`${closeStart} - ${closeEnd} ${$format('and', { default: 'and' })} ${farStart} - ${farEnd}`}
	class="mb-s"
	id="radio-ha5jnf3"
>
	{$format('distance_range_option_close_far', {
		default: 'Close and Far',
	})}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="6"
	size="m"
	info={`${midStart} - ${farEnd}`}
	class="mb-s"
	id="radio-zn8qrln"
>
	{$format('distance_range_option_mid_far', { default: 'Mid and Far' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="7"
	size="m"
	info={`${closeStart} - ${farEnd}`}
	class="mb-m"
	id="radio-unlefg4"
>
	{$format('distance_range_option_full', { default: 'Full' })}
</Radio>

<Button disabled={!initialized || !$app} size="m" width="full" on:click={setDistanceRange}>
	{$format('button_save', { default: 'Save' })}
</Button>
